import Link from '@/routing/Link'
import { CheckCircle2, Info, Loader2, AlertCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface CreditStatusBadgeProps {
  totalCost: number
  available: number
  allowOverage: boolean
  isLoading: boolean
}

/**
 * Displays credit balance status as a contextual badge.
 * Handles four states: loading, sufficient, overage, and insufficient.
 * Shared between study ReviewStep and audience ConfirmationStep.
 */
export function CreditStatusBadge({
  totalCost,
  available,
  allowOverage,
  isLoading,
}: CreditStatusBadgeProps) {
  const { t, i18n } = useTranslation('app')

  if (isLoading) {
    return (
      <div className="rounded-xl bg-muted dark:bg-[hsl(var(--card-border))]/30 p-3 flex items-center gap-3">
        <Loader2 className="h-5 w-5 text-muted-foreground animate-spin flex-shrink-0" />
        <span className="text-sm text-muted-foreground">{t('billing.costCard.checking')}</span>
      </div>
    )
  }

  const hasSufficientCredits = available >= totalCost
  const willUseOverage = !hasSufficientCredits && allowOverage
  const overageAmount = willUseOverage ? totalCost - available : 0
  const formattedAvailable = available.toLocaleString(i18n.language)

  if (hasSufficientCredits) {
    return (
      <div className="rounded-xl bg-primary/10 dark:bg-primary/20 p-3 flex items-center gap-3">
        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
        <div>
          <p className="text-sm font-semibold text-foreground dark:text-white">
            {t('billing.costCard.enough')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('billing.costCard.available', { formatted: formattedAvailable })}
          </p>
        </div>
      </div>
    )
  }

  if (willUseOverage) {
    return (
      <div className="rounded-xl bg-muted/50 dark:bg-muted/20 p-3 flex items-start gap-3">
        <Info className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-foreground dark:text-white">
            {t('billing.costCard.overage')}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('billing.costCard.overageBody', {
              formatted: overageAmount.toLocaleString(i18n.language),
            })}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-xl bg-destructive/10 border border-destructive/30 p-3 flex items-start gap-3">
      <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-destructive">{t('billing.costCard.insufficient')}</p>
        <p className="text-xs text-destructive/90 mt-0.5">
          {t('billing.costCard.needMore', {
            formatted: (totalCost - available).toLocaleString(i18n.language),
          })}
        </p>
        <Link href="/settings?tab=billing" className="text-xs text-destructive underline mt-1 block">
          {t('billing.costCard.manage')}
        </Link>
      </div>
    </div>
  )
}
